/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import clsx from 'classnames';

import a11yKeyPress from 'js/lib/a11yKeyPress';

import { MuiCollapse, Typography2, useId } from '@coursera/cds-core';

import { ChevronDownIcon } from 'bundles/ai-course-coach/assets/mui';

export type Props = {
  /**
   * Accessible description to be announced when component receives focus
   */
  'aria-label': string;

  /**
   * Content to be disclosed on expand.
   *
   * @example
   *
   * children={
   *   <Typography>
   *     Time flies like an arrow, fruit flies like a banana
   *   </Typography>
   * }
   */
  children: React.ReactNode;

  /**
   * Whether the disclosure is able to be collapsed/expanded.
   * Enabling this will keep the disclosure always expanded and remove the CTA toggle.
   * @default true
   */
  collapsible?: boolean;

  /**
   * Whether to render disclosure expanded by default.
   *
   * @default false
   */
  defaultExpanded?: boolean;

  /**
   * Header content for the disclosure. This will act as the CTA for expand/collapse.
   */
  header: React.ReactNode;

  /**
   * Additional content for the disclosure.
   * This will be visible at all times, regardless of expanded state.
   */
  footer?: React.ReactNode;

  /**
   * Callback when the disclosure is toggled i.e. expanded or collapsed
   */
  onToggle?: (expanded: boolean) => void;

  /**
   * ID for a11y - will be auto-generated internally if not passed in
   * @ignore
   */
  id?: string;
  className?: SerializedStyles;
};

const styles = css`
  &.disclosure {
    width: 100%;
    min-width: auto;
    border-radius: var(--cds-border-radius-200);
    background-color: var(--cds-color-emphasis-neutral-background-xweak);
  }

  .disclosure-header {
    padding: var(--cds-spacing-200) var(--cds-spacing-300);
    border-radius: var(--cds-border-radius-200);
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 1px var(--cds-color-interactive-stroke-primary-focus),
        0 0 0 2px var(--cds-color-interactive-stroke-primary-focus-invert);
    }
  }

  &.collapsible {
    .disclosure-header:hover {
      cursor: pointer;
    }
  }

  .disclosure-expand-icon {
    transition: transform 300ms ease-in-out;

    path {
      fill: var(--cds-color-grey-950);
    }
  }

  &.expanded {
    .disclosure-expand-icon {
      transform: rotate(180deg);
    }
  }

  .disclosure-content {
    padding: 0 var(--cds-spacing-300) 0 var(--cds-spacing-300);
    max-width: 1200px;
    margin-bottom: var(--cds-spacing-150);
  }

  .disclosure-footer {
    padding: 0 var(--cds-spacing-300) var(--cds-spacing-200) var(--cds-spacing-300);
    margin-top: var(--cds-spacing-150);
  }
`;

export const Disclosure = React.forwardRef((props: Props, ref: React.Ref<HTMLDivElement>) => {
  const {
    id,
    children,
    collapsible = true,
    defaultExpanded = true,
    header,
    footer,
    'aria-label': ariaLabel,
    className,
    onToggle,
  } = props;

  const [expanded, setExpanded] = useState<boolean>(collapsible ? defaultExpanded : true);

  const baseId = useId(id);
  const headerId = `${baseId}-header`;
  const contentId = `${baseId}-content`;
  const footerId = `${baseId}-footer`;

  const handleToggle = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onToggle?.(newExpanded);
  };

  const headerButtonProps = {
    tabIndex: 0,
    onClick: handleToggle,
    role: 'button',
    'aria-expanded': expanded,
    'aria-controls': contentId,
    onKeyPress: (e: React.KeyboardEvent) => a11yKeyPress(e, handleToggle),
  };

  return (
    <div ref={ref} className={clsx('disclosure', { expanded, collapsible })} id={baseId} css={[styles, className]}>
      <Typography2 component="h2" variant="titleLarge" aria-label={collapsible ? undefined : ariaLabel}>
        <div aria-label={ariaLabel} className="disclosure-header" id={headerId} {...(collapsible && headerButtonProps)}>
          {header}
          {collapsible && <ChevronDownIcon className="disclosure-expand-icon" />}
        </div>
      </Typography2>

      <MuiCollapse in={expanded} aria-hidden={!expanded}>
        <div id={contentId} className="disclosure-content">
          {children}
        </div>
        {footer && (
          <div id={footerId} className="disclosure-footer">
            {footer}
          </div>
        )}
      </MuiCollapse>
    </div>
  );
});

export default Disclosure;
